import { RequiredRule, Validator } from 'devextreme-react/validator';
import { DateBox } from 'devextreme-react/date-box';
import { useContext } from 'react';
import { Field, Panel } from '../../../../layouts';
import { AR, OrderStatus, ProductLine, ROType } from '../../../../types/enums';
import { formatDateWithTimeframe, formatShortDate, TimeframeType } from '../../../../utils/date-utils';
import { RetailOrderContext } from '../context/RetailOrderContext';

export default function InstallDatesPanel() {

	const { order, modifyOrder } = useContext(RetailOrderContext);

	const isOrderReadonly =
		order.details.orderStatusID === OrderStatus.Closed ||
		order.details.orderStatusID === OrderStatus.Cancelled ||
		order.details.orderStatusID === OrderStatus.Pending ||
		order.details.orderStatusID === OrderStatus.Hold;

	const isCabinetOrder = order.details.roTypeID === ROType.CabinetInstallOrder;

	return (
		<Panel header={'Dates'} >

			<Field label="Measure Date" visible={!order.details.isDeliveryOnly && !isCabinetOrder}>
				{formatDateWithTimeframe(order.details.measureDate, TimeframeType.Short)}
			</Field>
			<Field label="Template Date" visible={!order.details.isDeliveryOnly && !isCabinetOrder}>
				{formatDateWithTimeframe(order.details.enterTemplateDate, TimeframeType.Short)}
			</Field>
			<Field label={'Cabinet Install Date'} visible={!isCabinetOrder}>
				{formatShortDate(order.details.cabinetDate)}
			</Field>
			<Field label="Scheduled Install Date" visible={!order.details.isDeliveryOnly && !isCabinetOrder}>
				{formatDateWithTimeframe(order.details.scheduledInstallDate, TimeframeType.Long)}
			</Field>
			<Field label="Scheduled Install Start Date" visible={isCabinetOrder}>
				{formatShortDate(order.details.scheduledInstallStartDate)}
			</Field>
			<Field label="Scheduled Install Date" visible={isCabinetOrder}>
				{formatShortDate(order.details.scheduledInstallCompletionDate)}
			</Field>
			<Field label="Cabinet PO Received Date" visible={isCabinetOrder}>
				{formatShortDate(order.details.cabinetPOReceivedDate)}
			</Field>
			<Field label="Actual Install Date"
				visible={!order.details.isDeliveryOnly && !isCabinetOrder}>
				<DateBox inputAttr={{ 'data-testid': 'dtb-actual-install-date' }}
					value={order.details.actualInstallDate}
					onValueChanged={(e) => modifyOrder({ ...order.details, actualInstallDate: e.value })}
					readOnly={!order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) || isOrderReadonly}
					className={order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) ? 'required' : ''}
					min={order.details.roDate}
					max={new Date()}>
					<Validator>
						{order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) && order.modified &&
							<RequiredRule message={'Actual Install Date is required'} />}
					</Validator>
				</DateBox>
			</Field>
			<Field label="Actual Delivery Date"
				visible={order.details.isDeliveryOnly && !isCabinetOrder}>
				<DateBox value={order.details.deliveryDate}
					onValueChanged={(e) => modifyOrder({ ...order.details, deliveryDate: e.value })}
					readOnly={!order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) || isOrderReadonly}
					className={order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) ? 'required' : ''}
					min={order.details.roDate}
					max={new Date()}>
					<Validator>
						{order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) && order.modified &&
							<RequiredRule message={'Actual Delivery Date is required'} />}
					</Validator>
				</DateBox>
			</Field>
			<Field label="Actual Install Start Date"
				visible={isCabinetOrder}>
				<DateBox inputAttr={{ 'data-testid': 'dtb-actual-install-start-date' }}
					value={order.details.actualInstallStartDate}
					onValueChanged={(e) => modifyOrder({ ...order.details, actualInstallStartDate: e.value })}
					readOnly={!order.details.activeARs.includes(AR.InstallerToEnterActualInstallStartDate) || isOrderReadonly}
					className={order.details.activeARs.includes(AR.InstallerToEnterActualInstallStartDate) ? 'required' : ''}
					min={order.details.roDate}
					max={new Date()}>
					<Validator>
						{order.details.activeARs.includes(AR.InstallerToEnterActualInstallStartDate) && order.modified &&
							<RequiredRule message={'Actual Install Start Date is required'} />}
					</Validator>
				</DateBox>
			</Field>
			<Field label="Lower Cabinets Plumb & Level Date"
				visible={isCabinetOrder}>
				<DateBox inputAttr={{ 'data-testid': 'dtb-lower-cabinets-plumb-level-date' }}
					value={order.details.lowerCabinetsPlumbLevelDate}
					onValueChanged={(e) => modifyOrder({ ...order.details, lowerCabinetsPlumbLevelDate: e.value })}
					readOnly={!order.details.activeARs.includes(AR.InstallerToEnterLowerCabinetsPlumbLevelDate) || isOrderReadonly}
					className={order.details.activeARs.includes(AR.InstallerToEnterLowerCabinetsPlumbLevelDate) ? 'required' : ''}
					min={order.details.roDate}>
					<Validator>
						{order.details.activeARs.includes(AR.InstallerToEnterLowerCabinetsPlumbLevelDate) && order.modified &&
							<RequiredRule message={'Lower Cabinets Plumb & Level Date is required'} />}
					</Validator>
				</DateBox>
			</Field>
			<Field label="Actual Install Completion Date"
				visible={isCabinetOrder}>
				<DateBox inputAttr={{ 'data-testid': 'dtb-actual-install-completion-date' }}
					value={order.details.actualInstallCompletionDate}
					onValueChanged={(e) => modifyOrder({ ...order.details, actualInstallCompletionDate: e.value })}
					readOnly={!order.details.activeARs.includes(AR.InstallerToEnterActualInstallCompletionDate) || isOrderReadonly}
					className={order.details.activeARs.includes(AR.InstallerToEnterActualInstallCompletionDate) ? 'required' : ''}
					min={order.details.roDate}
					max={new Date()}>
					<Validator>
						{order.details.activeARs.includes(AR.InstallerToEnterActualInstallCompletionDate) && order.modified &&
							<RequiredRule message={'Actual Install Completion Date is required'} />}
					</Validator>
				</DateBox>
			</Field>
		</Panel>
	);
}
